import { useParams, Navigate } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import CookieBanner from "../components/common/CookieBanner";
import GuideArticle from "../components/guides/GuideArticle";
import useDocumentMeta from "../hooks/useDocumentMeta.js";
import usePageShell from "../hooks/usePageShell.js";
import usePageLanguage from "../hooks/usePageLanguage.js";
import {
  GUIDES,
  findGuideEntry,
  guideContentFor,
  guideHref,
  guideServedLanguage,
} from "../content/guides/registry.js";
import { guidePath, SITE_ORIGIN } from "../content/guides/guideModel.js";
import { alternatesFor } from "../content/pages/registry.js";

/**
 * /guides/:slug
 *
 * One route for every guide, not one component per guide. A guide is content,
 * and content that needs a hand-written React component is content that will
 * stop being written. Adding a guide is a registry entry and a content module;
 * this file does not change.
 *
 * ── Crawlability ──────────────────────────────────────────────────────────
 * Each guide also ships as a real static document (`guides/<slug>.html`, built
 * as its own Vite entry from `scripts/build-guides.mjs`). That file — not this
 * component — is what a crawler that does not run JavaScript receives, and it
 * carries the title, description, canonical, robots directive, social tags,
 * structured data and every word of the guide. React replaces it on mount.
 *
 * The meta hook still runs, because a visitor who arrives by client-side
 * navigation from another route gets no static head. It is given literal
 * strings rather than i18n keys: a guide's title and description belong to the
 * guide, which already knows which language it is written in, and putting ten
 * guides' worth of head copy into `meta.json` would split each guide's identity
 * across two files for no gain.
 *
 * ── Page shell ────────────────────────────────────────────────────────────
 * `usePageShell` — the shared bootstrap every page runs. See the hook for why
 * it re-initialises the Webflow engine from a layout effect rather than after
 * paint.
 */
const Guide = () => {
  const { slug } = useParams();

  // The language the URL is in — `/el/guides/...` is Greek, and an unprefixed
  // guide that HAS a Greek URL is English. `routeLanguage`'s rule, shared with
  // the head, the switcher and the analytics event so none of them can end up
  // describing this page as a different language than it is.
  const pageLanguage = usePageLanguage();

  const entry = findGuideEntry(slug);

  // The language this guide is actually SERVED in, which is not always the one
  // asked for: a reader with Greek selected who opens an English-only guide is
  // shown English, so its canonical and its URL must be English too.
  const language = entry ? guideServedLanguage(entry, pageLanguage) : pageLanguage;
  const guide = entry ? guideContentFor(entry, language) : null;

  // The head hook is a hook and cannot be called conditionally, so it runs with
  // undefined values for an unknown slug and simply does nothing.
  // The canonical is the guide's URL IN THE LANGUAGE BEING RENDERED, never the
  // English one — a Greek page canonicalising to English asks Google not to
  // index it. `alternatesFor` returns nothing until the guide genuinely has
  // both versions, so a guide never advertises a translation that does not
  // exist. Both come from the registry, so enabling Greek for a guide is one
  // content module and nothing here.
  useDocumentMeta({
    title: guide?.metaTitle,
    description: guide?.metaDescription,
    canonical: entry ? `${SITE_ORIGIN}${guideHref(entry, language)}` : undefined,
    robots: guide ? "index, follow" : undefined,
    // Keyed on the IDENTITY path, so both language versions of a guide resolve
    // the same registry page and therefore emit the same reciprocal set.
    alternates: entry ? alternatesFor(guidePath(entry.slug)) : undefined,
  });

  usePageShell({ enabled: Boolean(guide) });

  // An unregistered slug is not a guide. Send it home rather than rendering an
  // empty article shell — the catch-all route does the same for every other
  // unknown path on the site.
  if (!guide) return <Navigate to="/" replace />;

  // Related guides are resolved from the registry, never from free text, so a
  // link here cannot point at a guide that does not exist.
  const relatedGuides = (guide.related?.guides || [])
    .map((relatedSlug) => findGuideEntry(relatedSlug))
    .filter(Boolean)
    .map((relatedEntry) => ({
      ...guideContentFor(relatedEntry, language),
      href: guideHref(relatedEntry, language),
    }));

  return (
    <div className="page-wrapper wps-page guide-page">
      <Navbar />
      <CookieBanner />
      <main className="main">
        <GuideArticle
        guide={guide}
        relatedGuides={relatedGuides}
        language={language}
      />
      </main>
      <Footer />
    </div>
  );
};

export default Guide;

/** Exported for the test, which asserts the route renders every registered
 *  guide rather than a hand-maintained subset of them. */
export const REGISTERED_GUIDES = GUIDES;
